import DOMPurify from 'dompurify'

/**
 * Announcement bodies: sanitising them, and telling rich text apart from the plain
 * text every body used to be (VRG-332).
 *
 * ## Why this file exists at all
 *
 * `AnnouncementCard` carried an explicit note explaining that the body was plain text
 * rendered with `white-space: pre-line`, "without accepting HTML — the body is authored
 * in a plain textarea and rendering it as markup would make the internal form an
 * injection surface into every brand's app."
 *
 * That reasoning is still exactly right. The requirement to accept rich text does not
 * refute it; it converts it from a design constraint into a mitigation that has to be
 * real. An announcement is written by Vera staff and rendered, unmodified, in the
 * console of every brand — so a compromised internal account (or a staffer pasting
 * markup from somewhere else) is authoring into everyone's page at once. There is no
 * per-tenant blast-radius limit here to fall back on.
 *
 * ## Sanitise on RENDER, not only on save
 *
 * Filtering at the write boundary alone would be the cheaper-looking option and is
 * wrong for one specific reason: every row already in the database was written before
 * this change, by a code path that never validated anything. A save-time filter trusts
 * data it has never seen. It also means the guarantee lives in whichever API version
 * happened to write a given row — so a bug in one release leaves permanently
 * unsanitised rows behind it, and a future direct DB write (a seed, a migration, a
 * support fix in psql) bypasses it entirely.
 *
 * Sanitising at render is idempotent, applies uniformly to every row regardless of
 * provenance, and puts the check on the same side of the wire as the `innerHTML` that
 * makes it necessary. The two are not exclusive — a write-side filter is a fine
 * addition — but the render-side one is the one that cannot be bypassed by data.
 */

/**
 * Tags an announcement body may contain. Everything else is stripped.
 *
 * An ALLOW-list, never a deny-list: the set of dangerous markup is open-ended and
 * grows with every browser feature, while the set of things an announcement legitimately
 * needs is short and closed. This is what the editor's toolbar can actually produce —
 * paragraphs and breaks, bold/italic/underline/strike, links, ordered and unordered
 * lists, headings, blockquote, inline and block code.
 *
 * NOTABLY ABSENT, each for a reason rather than an oversight:
 *
 *   - `script`, `style` — arbitrary execution, and (for `style`) selectors that reach
 *     out of the card and restyle the surrounding app.
 *   - `iframe`, `object`, `embed`, `frame` — embed a third-party origin inside the
 *     brand's console.
 *   - `form`, `input`, `button`, `textarea` — a credential prompt rendered inside the
 *     real product is the most convincing phishing surface there is.
 *   - `img`, `svg`, `video`, `audio` — not execution, but a REQUEST: a remote `src`
 *     fires on render and hands the reader's IP, user-agent and read-time to whoever
 *     controls the host, for every brand that loads the feed. An announcement has never
 *     needed an image, so allowing one buys a tracking pixel and nothing else.
 *   - `table` and friends — the editor registers the extension but exposes no toolbar
 *     control for it, so nothing can author one; allowing tags nothing produces would
 *     be widening the surface for free.
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
]

/**
 * Attributes that survive. Everything else — including every `on*` handler, `style`,
 * `srcset`, `formaction` and any `data-*` — is dropped.
 *
 * The event handlers are the point. `<b onmouseover="…">` needs no script tag and no
 * network request; it is inline execution hanging off a tag that IS on the allow-list,
 * which is why an allow-list of TAGS alone is not a mitigation. DOMPurify drops any
 * attribute not named here, so the handlers go with them rather than being enumerated.
 */
const ALLOWED_ATTR = ['href', 'target', 'rel']

/**
 * Schemes a link may use.
 *
 * `javascript:` is the one that matters — `<a href="javascript:…">` is execution on
 * click, through an anchor that is otherwise entirely legitimate, and it is why an
 * `href` allow-list without a scheme allow-list is not enough. `data:` is excluded for
 * the same family of reason (a `data:text/html` link navigates to attacker-authored
 * markup on this origin's coat-tails).
 *
 * `http(s)` for external references, `mailto` for a contact address, and a leading `/`
 * for the in-app routes an announcement most often points at.
 */
const ALLOWED_URI_REGEXP = /^(?:https?:|mailto:|\/|#)/i

/**
 * Every external link leaves with `rel="noopener noreferrer"` and `target="_blank"`.
 *
 * `noopener` is a security fix, not a nicety: without it the opened page receives a
 * live `window.opener` handle to the console it was opened from and can navigate it
 * anywhere — reverse tabnabbing, where the tab the user comes back to is no longer the
 * app they left. `noreferrer` keeps the console's URL (which carries record ids) out of
 * the destination's referrer log.
 *
 * IN-APP links (`/…`, `#…`) are left alone: they are same-origin, so there is nothing to
 * isolate, and forcing `target="_blank"` on them would open a second copy of the app in
 * a new tab instead of navigating.
 *
 * Registered once at module load rather than per call. DOMPurify hooks are global, so
 * adding one inside `sanitizeAnnouncementBody` would stack a duplicate on every render
 * — a slow leak that only shows up as the feed getting heavier the longer it is open.
 */
let hookRegistered = false
const registerLinkHook = () => {
  if (hookRegistered) return
  hookRegistered = true
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    if (node.tagName !== 'A') return
    const href = node.getAttribute('href') ?? ''
    if (/^https?:/i.test(href)) {
      node.setAttribute('target', '_blank')
      node.setAttribute('rel', 'noopener noreferrer')
    } else {
      // An in-app or mailto link. Strip any target the author's markup carried rather
      // than trusting it — `target="_blank"` on a same-origin route is the tabnabbing
      // shape again, just pointed inward.
      node.removeAttribute('target')
      node.removeAttribute('rel')
    }
  })
}

/**
 * Does this body contain markup the renderer should PARSE, or is it the plain text
 * every announcement used to be?
 *
 * Deliberately narrow: it matches only an opening tag drawn from the allow-list above.
 * The obvious `/<[a-z]/i` is too eager — a legacy body that says "wrap it in <div> tags"
 * is prose, and treating it as markup would silently delete the words the sentence is
 * about.
 *
 * SAFETY DOES NOT DEPEND ON THIS ANSWER, which is what makes a heuristic acceptable
 * here. Both branches are XSS-safe and the function only chooses between them:
 *
 *   - true  → the string is sanitised and set as `innerHTML`.
 *   - false → React renders it as a TEXT NODE, which escapes it. A legacy body
 *             containing `<script>` or `<img onerror=…>` matches nothing here and is
 *             printed literally, which is both safe and the honest rendering of what
 *             the author typed.
 *
 * So the worst case is a cosmetic one (an odd-looking body), never an executable one.
 */
export const isRichTextBody = (body: string): boolean =>
  new RegExp(`<(?:${ALLOWED_TAGS.join('|')})\\b[^>]*>`, 'i').test(body)

/**
 * The body, safe to hand to `dangerouslySetInnerHTML`.
 *
 * Call this at EVERY render site. It is cheap, it is idempotent, and the moment one
 * surface renders the raw string instead, the guarantee is gone for that surface only —
 * which is the hardest kind of hole to notice, because the other three still look fine.
 */
export const sanitizeAnnouncementBody = (body: string): string => {
  registerLinkHook()
  return DOMPurify.sanitize(body, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    // Belt and braces beside the allow-list. `ALLOWED_TAGS` already excludes these, but
    // naming the executable ones explicitly means a future widening of the allow-list
    // cannot readmit them by accident.
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    // Return a string, not a DocumentFragment — this feeds `dangerouslySetInnerHTML`.
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as unknown as string
}

/**
 * A one-line, markup-free preview — for a table cell or any other place the body is
 * shown as text rather than rendered.
 *
 * `ALLOWED_TAGS: []` makes DOMPurify strip every tag and keep the text, so a rich-text
 * body reads as words in the internal list instead of `<p>Export any…</p>`. The
 * whitespace collapse is what keeps it to one line: a body with paragraph breaks would
 * otherwise render its newlines inside a fixed-height cell.
 */
export const announcementBodyPreview = (body: string): string =>
  (
    DOMPurify.sanitize(body, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    }) as unknown as string
  )
    .replace(/\s+/g, ' ')
    .trim()

/**
 * A legacy PLAIN-TEXT body, converted to the paragraph markup the editor speaks.
 *
 * Needed only when SEEDING the editor, and it is not cosmetic — it prevents data loss.
 * tiptap parses whatever string it is given as HTML, and HTML collapses newlines to
 * spaces. So loading a legacy body straight in would flatten every paragraph break into
 * one run-on paragraph, and the staffer's next Save would persist that flattening. The
 * announcement would come back visibly worse from an edit that never touched the body,
 * with nothing on screen to say what happened.
 *
 * Escapes first, so text that merely LOOKS like markup ("use <div> tags") survives as
 * the words the author wrote rather than becoming a tag on the way in.
 *
 * Blank-line-separated runs become paragraphs and single newlines become `<br>`, which
 * is the same distinction `white-space: pre-line` was drawing visually — so an
 * announcement seeded into the editor and saved unchanged renders identically to before.
 */
export const plainTextBodyToHtml = (body: string): string => {
  const escaped = body
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  return escaped
    .split(/\n{2,}/)
    .map(block => `<p>${block.replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/**
 * Does the body have any actual TEXT in it?
 *
 * `body.trim()` is the obvious check and is wrong once the editor is rich text: an empty
 * tiptap document serialises to `<p></p>`, which is four characters of non-whitespace,
 * so a "required" check against the raw string passes on a completely empty body and the
 * Save button goes live over nothing. This strips the markup first and asks the real
 * question.
 */
export const announcementBodyHasText = (body: string): boolean =>
  announcementBodyPreview(body).length > 0
